// keywordService.js
import { doc, getDoc, getDocs, writeBatch, collection } from 'firebase/firestore';
import { db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
// import { writeBatch, getDocs, getDoc } from 'firebase/firestore';

class KeywordService {
  constructor() {
    this.cachedKeywords = null;
    this.functions = getFunctions();
    this.keywordsCollection = 'keywords';
  }

  /**
   * Get all keywords with job counts
   * @returns {Promise<Array<{keyword: string, jobIds: string[], count: number}>>}
   */
  async getAllKeywords() {
    if (this.cachedKeywords) {
      return this.cachedKeywords;
    }

    try {
      const keywordsDoc = await getDoc(doc(db, 'metadata', 'searchKeywords'));
      
      if (keywordsDoc.exists()) {
        const data = keywordsDoc.data();
        this.cachedKeywords = data.keywords || [];
        // console.log(`Loaded ${this.cachedKeywords.length} keywords`);
        return this.cachedKeywords;
      } else {
        console.warn('Keywords document not found. Run aggregateKeywords first.');
        return [];
      }
    } catch (error) {
      console.error('Error fetching keywords:', error);
      return [];
    }
  }

  /**
   * Get suggested keywords matching search term
   * @param {string} searchTerm 
   * @param {number} limit 
   * @returns {Promise<Array<{keyword: string, jobIds: string[], count: number}>>}
   */
  async getSuggestedKeywords(searchTerm, limit = 10) {
    const allKeywords = await this.getAllKeywords();
    
    if (!searchTerm || searchTerm.trim() === '') {
      return allKeywords.slice(0, limit); // Return most popular keywords
    }

    const lowerSearch = searchTerm.toLowerCase();
    
    // Find matching keywords
    const matches = allKeywords.filter(keywordObj => 
      keywordObj.keyword.includes(lowerSearch)
    );

    // Prioritize keywords that start with the search term
    const startsWithMatches = matches.filter(k => k.keyword.startsWith(lowerSearch));
    const containsMatches = matches.filter(k => !k.keyword.startsWith(lowerSearch));

    return [...startsWithMatches, ...containsMatches].slice(0, limit);
  }

  /**
   * Get job IDs for a specific keyword
   * @param {string} keyword 
   * @returns {Promise<string[]>}
   */
  async getJobIdsForKeyword(keyword) {
    const allKeywords = await this.getAllKeywords();
    const keywordObj = allKeywords.find(k => k.keyword === keyword.toLowerCase());
    return keywordObj ? keywordObj.jobIds : [];
  }

  /**
   * Trigger keyword aggregation
   */
  async aggregateKeywords() {
    try {
      const aggregateKeywords = httpsCallable(this.functions, 'aggregateKeywords');
      const result = await aggregateKeywords();
      
      this.cachedKeywords = null; // Clear cache
      
      return result.data;
    } catch (error) {
      console.error('Error aggregating keywords:', error);
      throw error;
    }
  }

  clearCache() {
    this.cachedKeywords = null;
  }

  /**
 * Remove a job ID from all its associated keywords
 * @param {string} jobId - Job ID to remove
 * @param {Array<string>} keywords - Keywords to clean up
 * @returns {object} - { success: boolean, error?: string }
 */
async removeJobFromKeywords(jobId, keywords) {
  try {
    const batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH_SIZE = 500; // Firestore limit
    
    // console.log(`Removing job ${jobId} from ${keywords.length} keywords...`);
    
    for (const keyword of keywords) {
      const keywordRef = doc(db, this.keywordsCollection, keyword.toLowerCase());
      const keywordSnap = await getDoc(keywordRef);
      
      if (keywordSnap.exists()) {
        const data = keywordSnap.data();
        const updatedJobIds = data.jobIds.filter(id => id !== jobId);
        
        if (updatedJobIds.length === 0) {
          // No more jobs use this keyword - delete it
          batch.delete(keywordRef);
          // console.log(`  ❌ Deleting keyword "${keyword}" (no more jobs)`);
        } else {
          // Update with remaining job IDs
          batch.update(keywordRef, {
            jobIds: updatedJobIds,
            count: updatedJobIds.length
          });
          // console.log(`  ✏️ Updated keyword "${keyword}" (${data.jobIds.length} → ${updatedJobIds.length} jobs)`);
        }
        
        batchCount++;
        
        // Commit batch if we hit the limit
        if (batchCount >= MAX_BATCH_SIZE) {
          await batch.commit();
          // console.log(`Committed batch of ${batchCount} operations`);
          batchCount = 0;
        }
      }
    }
    
    // Commit remaining operations
    if (batchCount > 0) {
      await batch.commit();
      // console.log(`Committed final batch of ${batchCount} operations`);
    }
    
    // console.log('✅ Keyword cleanup complete');
    return { success: true };
  } catch (error) {
    console.error('Error removing job from keywords:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Clean up ALL orphaned keywords (one-time cleanup script)
 * Removes keywords that reference non-existent jobs
 * @returns {object} - { success: boolean, deletedCount: number, updatedCount: number }
 */
async cleanupOrphanedKeywords() {
  try {
    // console.log('🧹 Starting orphaned keywords cleanup...');
    
    const keywordsSnapshot = await getDocs(collection(db, this.keywordsCollection));
    const batch = writeBatch(db);
    let batchCount = 0;
    let deletedCount = 0;
    let updatedCount = 0;
    
    for (const keywordDoc of keywordsSnapshot.docs) {
      const data = keywordDoc.data();
      const jobIds = data.jobIds || [];
      
      // Check which jobs actually exist
      const validJobIds = [];
      for (const jobId of jobIds) {
        const jobRef = doc(db, 'jobs', jobId);
        const jobSnap = await getDoc(jobRef);
        if (jobSnap.exists()) {
          validJobIds.push(jobId);
        }
      }
      
      if (validJobIds.length === 0) {
        // Delete keyword with no valid jobs
        batch.delete(doc(db, this.keywordsCollection, keywordDoc.id));
        deletedCount++;
        // console.log(`  ❌ Deleting orphaned keyword: ${keywordDoc.id}`);
      } else if (validJobIds.length !== jobIds.length) {
        // Update keyword with only valid jobs
        batch.update(doc(db, this.keywordsCollection, keywordDoc.id), {
          jobIds: validJobIds,
          count: validJobIds.length
        });
        updatedCount++;
        // console.log(`  ✏️ Updated keyword: ${keywordDoc.id} (${jobIds.length} → ${validJobIds.length} jobs)`);
      }
      
      batchCount++;
      
      if (batchCount >= 500) {
        await batch.commit();
        // console.log(`Committed batch of ${batchCount} operations`);
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    
    // console.log(`✅ Cleanup complete! Deleted ${deletedCount} keywords, updated ${updatedCount} keywords`);
    return { success: true, deletedCount, updatedCount };
  } catch (error) {
    console.error('Error cleaning up orphaned keywords:', error);
    return { success: false, error: error.message };
  }
}
}

export default new KeywordService();